import {
  APP_DISPLAY_NAME,
  ModuleStoreState,
  setupCompanyRequestSchema,
  type ProductCode,
} from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Navigate } from 'react-router';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { useMe } from '@/platform/auth/use-me';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { BrandAccentEffect } from '@/platform/brand/brand-accent-effect';
import { useSetupCompany, useSetupStatus } from '@/platform/brand/use-setup';
import {
  brandDisplayName,
  publicBrandLogoSrc,
  usePublicBrand,
} from '@/platform/brand/use-public-brand';
import { adminPanelClassName } from '@/platform/features/admin-panel';
import { moduleStoreStatusLabel } from '@/platform/features/modules/module-store-status';
import { useCompleteSetup, useModuleStore } from '@/platform/features/modules/use-module-store';
import { START_PATH } from '@/platform/lib/products';

function suggestSlug(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return slug || 'firma';
}

export function EinrichtenPage() {
  usePageTitle('Einrichten');
  const copy = useVoiceCopy();
  const status = useSetupStatus();
  const me = useMe();
  const brand = usePublicBrand();
  const setupCompany = useSetupCompany();
  const completeSetup = useCompleteSetup();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [companyCreated, setCompanyCreated] = useState(false);
  const [selected, setSelected] = useState<Set<ProductCode>>(() => new Set());

  const hasTenant = companyCreated || status.data?.hasTenant === true;
  const showCompanyForm = !hasTenant;
  const showModulePicker = hasTenant && status.data?.setupCompleted !== true;
  const catalog = useModuleStore({ enabled: Boolean(showModulePicker && me.data) });

  if (status.isPending || (hasTenant && me.isPending)) {
    return (
      <div className="flex justify-center py-16">
        <Loader2Icon className="size-8 animate-spin text-muted-foreground" aria-label="Laden" />
      </div>
    );
  }

  if (status.data?.setupCompleted) {
    return <Navigate to={START_PATH} replace />;
  }

  if (showModulePicker && !me.data) {
    return <Navigate to={`/anmelden?from=${encodeURIComponent('/einrichten')}`} replace />;
  }

  async function onSubmitCompany(event: FormEvent) {
    event.preventDefault();
    setError(null);
    const parsed = setupCompanyRequestSchema.safeParse({
      name,
      slug,
      email,
      firstName,
      lastName,
      password,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Bitte die Angaben prüfen.');
      return;
    }
    try {
      await setupCompany.mutateAsync(parsed.data);
      setCompanyCreated(true);
      window.location.assign(`/anmelden?from=${encodeURIComponent('/einrichten')}`);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Speichern fehlgeschlagen.');
    }
  }

  function toggleCode(code: ProductCode, checked: boolean) {
    setSelected((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(code);
      } else {
        next.delete(code);
      }
      return next;
    });
  }

  async function onComplete() {
    setError(null);
    try {
      await completeSetup.mutateAsync({ codes: [...selected] });
      window.location.assign(START_PATH);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Abschluss fehlgeschlagen.');
    }
  }

  const brandName = brandDisplayName(brand.data);
  const logoSrc = publicBrandLogoSrc(brand.data?.logoUrl);

  return (
    <>
      <BrandAccentEffect />
      <div className="mx-auto flex w-full max-w-lg flex-col gap-6">
        <div className="text-center">
          {logoSrc ? (
            <img
              src={logoSrc}
              alt={brandName || APP_DISPLAY_NAME}
              className="mx-auto h-16 w-auto"
            />
          ) : brandName ? (
            <p className="text-xl font-semibold">{brandName}</p>
          ) : null}
          <p className="mt-2 text-sm text-muted-foreground">{APP_DISPLAY_NAME} einrichten</p>
        </div>

        {error ? (
          <p role="alert" className="text-center text-sm text-destructive">
            {error}
          </p>
        ) : null}

        {showCompanyForm ? (
          <Card>
            <CardHeader>
              <CardTitle>Firma und Administrator</CardTitle>
              <CardDescription>{copy('platform.setup.org')}</CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={(event) => void onSubmitCompany(event)}>
                <div className="space-y-1.5">
                  <Label htmlFor="company-name">Firmenname</Label>
                  <Input
                    id="company-name"
                    value={name}
                    onChange={(event) => {
                      const next = event.target.value;
                      setName(next);
                      if (!slugTouched) {
                        setSlug(suggestSlug(next));
                      }
                    }}
                    required
                    autoFocus
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="company-slug">Slug (Host-Kennung)</Label>
                  <Input
                    id="company-slug"
                    value={slug}
                    onChange={(event) => {
                      setSlugTouched(true);
                      setSlug(event.target.value);
                    }}
                    required
                    autoComplete="off"
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="admin-first">Vorname</Label>
                    <Input
                      id="admin-first"
                      value={firstName}
                      onChange={(event) => setFirstName(event.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="admin-last">Nachname</Label>
                    <Input
                      id="admin-last"
                      value={lastName}
                      onChange={(event) => setLastName(event.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="admin-email">E-Mail</Label>
                  <Input
                    id="admin-email"
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="admin-password">Passwort</Label>
                  <Input
                    id="admin-password"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={setupCompany.isPending}>
                  {setupCompany.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
                  Firma anlegen
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : null}

        {showModulePicker && me.data ? (
          <Card>
            <CardHeader>
              <CardTitle>Module</CardTitle>
              <CardDescription>{copy('platform.setup.modules')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {catalog.isPending ? (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2Icon className="size-4 animate-spin" aria-hidden />
                  Katalog wird geladen …
                </p>
              ) : null}
              {catalog.isError ? (
                <p role="alert" className="text-sm text-destructive">
                  {catalog.error instanceof ApiRequestError
                    ? catalog.error.message
                    : 'Der Katalog konnte nicht geladen werden.'}
                </p>
              ) : null}
              {catalog.data ? (
                <ul className="space-y-3">
                  {catalog.data.items.map((card) => {
                    const selectable = card.installable;
                    const checked = selected.has(card.code);
                    const checkboxId = `setup-module-${card.code}`;
                    return (
                      <li key={card.code} className={adminPanelClassName}>
                        <div className="flex items-start gap-3">
                          {selectable ? (
                            <Checkbox
                              id={checkboxId}
                              checked={checked}
                              disabled={completeSetup.isPending}
                              onCheckedChange={(value) => toggleCode(card.code, value === true)}
                              className="mt-1"
                            />
                          ) : (
                            <span className="mt-1 size-4 shrink-0" aria-hidden />
                          )}
                          <div className="min-w-0 space-y-1">
                            <Label
                              htmlFor={selectable ? checkboxId : undefined}
                              className="font-medium"
                            >
                              {card.labelDe}
                            </Label>
                            <p className="text-sm text-muted-foreground">{card.descriptionDe}</p>
                            <p className="text-xs text-muted-foreground">
                              {card.state === ModuleStoreState.PLANNED
                                ? 'Noch nicht verfügbar'
                                : moduleStoreStatusLabel(card)}
                            </p>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
              <Button
                type="button"
                className="w-full"
                disabled={completeSetup.isPending || catalog.isPending || catalog.isError}
                onClick={() => void onComplete()}
              >
                {completeSetup.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
                Einrichtung abschließen
              </Button>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </>
  );
}
